import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useMemo } from 'react';

import { useSchool } from '@/providers/session/SessionProvider';
import { useTranslation } from 'react-i18next';

import { StudentResult } from '@/hooks/types/result';
import React from 'react';

import { PdfFrame, usePdfGenerator } from '@/experiments/ResultReport/PdfGenerator';
import { ExportableResultSlide } from './ExportableResultSlide';

import { PDF_SIZE } from '@/constant';
import { capture } from '@/handlers/error';
import { Question, legacyQuestionComponentType } from '@/types/slide';
import { useNotifications } from '@toolpad/core';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import JSZip from 'jszip';

export const ExportResultsDialog = (props: { open: boolean; onClose: () => void; data: StudentResult[] }) => {
    const { t } = useTranslation();

    const { generatePdf } = usePdfGenerator();

    const notifications = useNotifications();

    const { school } = useSchool();
    const theme: Theme = useTheme();

    const [lessonTheme, setLessonTheme] = React.useState<string | undefined>(undefined);
    const [targetLanguageFeatures, setTargetLanguageFeatures] = React.useState<string | undefined>(undefined);

    const [answersPerPage, setAnswersPerPage] = React.useState<1 | 2 | 3 | 4>(1);

    const [isGenerating, setIsGenerating] = React.useState<boolean>(false);

    const shouldContinueExport = React.useRef(true);

    React.useEffect(() => {
        if (props.open) {
            shouldContinueExport.current = true;
        }
    }, [props.open]);

    React.useEffect(() => {
        return () => {
            shouldContinueExport.current = false;
        };
    }, []);

    const exportResults = async () => {
        const pdfs: { filename: string; file: jsPDF }[] = [];

        setIsGenerating(true);

        try {
            for (const studentResult of props.data) {
                if (!shouldContinueExport.current) {
                    break;
                }

                const studentReportSlides = studentsReportSlides[studentResult.id];

                if (studentReportSlides) {
                    const pdf = await generatePdf(studentReportSlides, {
                        title: school?.name ?? '',
                        data: {
                            name: studentResult.studentName,
                            theme: lessonTheme ?? '',
                            class: studentResult.className,
                            targetLanguageFeatures: targetLanguageFeatures ?? '',
                        },
                        answersPerPage: answersPerPage,
                    });

                    if (pdf) {
                        pdfs.push({ filename: `${studentResult.studentName}-${studentResult.studentNumber}.pdf`, file: pdf });
                    }
                }
            }

            if (pdfs.length > 0 && shouldContinueExport.current) {
                const zip = new JSZip();

                pdfs.forEach(({ filename, file }) => {
                    zip.file(filename, file.output('blob'));
                });

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                saveAs(zipBlob, `Results-Printout.zip`);
            }
        } catch (error) {
            capture(error);
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
        } finally {
            setIsGenerating(false);
        }
    };

    // show legacy result cards
    const studentsReportSlides = useMemo(() => {
        const ret: Record<string, Question[]> = {};

        props.data.forEach((studentResult) => {
            const reportSlides: Question[] = [];

            for (const slide of studentResult.slides) {
                for (const question of slide.questions) {
                    if (legacyQuestionComponentType.some((t) => t === question.type)) {
                        reportSlides.push(question);
                    }
                }
            }
            ret[studentResult.id] = reportSlides;
        });

        return ret;
    }, [props.data]);

    const PreviewFrame = React.useMemo(() => {
        // currently only show previews for first student
        if (props.data.length > 0) {
            const studentResult = props.data[0];
            if (studentResult) {
                const studentReportSlides = studentsReportSlides[studentResult.id];

                let collected = 0;

                const slides: React.ReactNode[] = [];

                if (studentReportSlides) {
                    for (const reportSlide of studentReportSlides) {
                        if (collected < answersPerPage) {
                            slides.push(<ExportableResultSlide question={reportSlide} />);
                        }
                        collected++;

                        if (collected >= answersPerPage) {
                            break;
                        }
                    }
                }

                const frameData = {
                    name: studentResult.studentName,
                    theme: lessonTheme ?? '',
                    class: studentResult.className,
                    targetLanguageFeatures: targetLanguageFeatures ?? '',
                };

                return (
                    <PdfFrame theme={theme} title={school?.name ?? ''} data={frameData} answersPerPage={answersPerPage}>
                        {slides}
                    </PdfFrame>
                );
            }
        }

        return <></>;
    }, [props.data, answersPerPage, lessonTheme, theme, targetLanguageFeatures, school?.name, studentsReportSlides]);

    return (
        <Dialog fullWidth maxWidth='lg' open={props.open} onClose={props.onClose}>
            <DialogTitle>
                {t('result.exportResults.title')}
                <Typography variant='body1' sx={{ py: 1 }}>
                    {t('result.exportResults.description')}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                    }}
                >
                    <Box
                        sx={{
                            width: '30%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            pr: 3,
                            pt: 1,
                            pb: 2,
                        }}
                    >
                        <Stack spacing={2} sx={{ alignItems: 'center' }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('result.exportResults.layout')}</InputLabel>
                                <Select
                                    labelId='layout-select-label'
                                    data-testid='layout-select'
                                    id='layout-select'
                                    value={answersPerPage}
                                    label={t('result.exportResults.layout')}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setAnswersPerPage(value);
                                    }}
                                >
                                    <MenuItem value={1}>{t('result.exportResults.layout1')}</MenuItem>
                                    <MenuItem value={2}>{t('result.exportResults.layout2')}</MenuItem>
                                    <MenuItem value={3}>{t('result.exportResults.layout3')}</MenuItem>
                                    <MenuItem value={4}>{t('result.exportResults.layout4')}</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label={t('result.exportResults.theme')}
                                value={lessonTheme}
                                onChange={(e) => {
                                    setLessonTheme(e.target.value);
                                }}
                                fullWidth
                            />

                            <TextField
                                multiline
                                label={t('result.exportResults.targetLanguageFeatures')}
                                value={targetLanguageFeatures}
                                onChange={(e) => {
                                    setTargetLanguageFeatures(e.target.value);
                                }}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 200,
                                        alignItems: 'flex-start',
                                    },
                                    '& .MuiInputBase-input': {
                                        height: '100% !important',
                                        overflow: 'auto !important',
                                    },

                                    '& .MuiInputBase-inputMultiline': {
                                        overflow: 'auto',
                                        resize: 'none',
                                    },
                                }}
                            />
                        </Stack>
                    </Box>

                    <Box
                        sx={(theme) => ({
                            width: '70%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '4px',
                            backgroundColor: '#eee',

                            ...theme.applyStyles('dark', {
                                backgroundColor: '#aaa',
                            }),
                            position: 'relative',

                            minHeight: 600,

                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            mt: 1,
                        })}
                    >
                        <Box
                            sx={{
                                transformOrigin: 'center',
                                width: PDF_SIZE.width,
                                height: PDF_SIZE.height,
                                position: 'absolute',

                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) scale(0.6)',
                                willChange: 'transform',
                            }}
                        >
                            {PreviewFrame}
                        </Box>

                        <Stack spacing={1} sx={{ flex: 1, alignItems: 'center', width: '100%' }}>
                            <Typography variant='body1' sx={{ pt: 1, whiteSpace: 'pre-wrap' }}>
                                {t('result.exportResults.previewDescription')}
                            </Typography>
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>{t('common.cancel')}</Button>
                <Button
                    variant='contained'
                    loading={isGenerating}
                    disabled={isGenerating}
                    onClick={() => {
                        exportResults();
                    }}
                >
                    {t('result.exportResults.compile')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportResultsDialog;
